// Per-product list/floor price editor — the 4 tiers (government, ngo,
// corporate, retail) laid out as 4 compact rows rather than 4 columns per
// product row in the main table (CatalogPage's own header comment: "four
// tiers per product is a lot of columns for one row"). Expanded inline
// under a product row on demand, same "expand near the trigger" language as
// ProductForm.jsx/ProductPicker.jsx.
//
// Floor <= list is enforced HERE, client-side, before the request is even
// sent (root CLAUDE.md: "Enforce floor <= list client-side too... not just
// relying on the server's FLOOR_EXCEEDS_LIST 400") — see validate() below —
// and again, authoritatively, by catalogService.upsertPriceTier() server-
// side; this is a fast-feedback duplicate of that rule, not a replacement
// for it.
//
// This panel edits list_price/floor_price ONLY. No cost_price field exists
// here, or anywhere in this component's imports — cost is a different
// screen (adminCatalogRoutes.js's separate PUT .../cost route).

import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext.jsx';
import { useUpsertPriceTier } from '../hooks/useAdminCatalog.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import { Spinner } from 'shared/components/Feedback/index.js';
import styles from './PriceTierPanel.module.css';

const TIER_ORDER = ['government', 'ngo', 'corporate', 'retail'];

function TierRow({ productId, tier, existing }) {
  const { t } = useLanguage();
  const mutation = useUpsertPriceTier();
  const { toastOk, toastErr } = useActionFeedback();
  const [listPrice, setListPrice] = useState(existing?.listPrice ?? '');
  const [floorPrice, setFloorPrice] = useState(existing?.floorPrice ?? '');
  const [errorId, setErrorId] = useState(null);

  const handleSave = () => {
    setErrorId(null);

    const listTrimmed = listPrice.toString().trim();
    const floorTrimmed = floorPrice.toString().trim();
    if (!listTrimmed || !floorTrimmed) {
      setErrorId('catalog.tiers.bothRequired');
      return;
    }
    if (Number(floorTrimmed) > Number(listTrimmed)) {
      setErrorId('catalog.tiers.floorExceedsListClient');
      return;
    }

    mutation.mutate(
      { productId, tier, listPrice: listTrimmed, floorPrice: floorTrimmed },
      {
        onSuccess: () =>
          toastOk(t('catalog.tiers.savedToast', { tier: t(`workspace.reference.tier.${tier}`) })),
        onError: (err) => toastErr(err, ['FLOOR_EXCEEDS_LIST', 'VALIDATION_ERROR']),
      }
    );
  };

  return (
    <div className={styles.tierRow}>
      <span className={styles.tierLabel}>{t(`workspace.reference.tier.${tier}`)}</span>
      <label className={styles.priceField}>
        <span className={styles.priceFieldLabel}>{t('catalog.tiers.listLabel')}</span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={listPrice}
          onChange={(e) => setListPrice(e.target.value)}
          className={`${styles.priceInput} tabularNums`}
        />
      </label>
      <label className={styles.priceField}>
        <span className={styles.priceFieldLabel}>{t('catalog.tiers.floorLabel')}</span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={floorPrice}
          onChange={(e) => setFloorPrice(e.target.value)}
          className={`${styles.priceInput} tabularNums`}
        />
      </label>
      <button
        type="button"
        className={styles.saveButton}
        onClick={handleSave}
        disabled={mutation.isPending}
      >
        {mutation.isPending && <Spinner />}{' '}
        {mutation.isPending ? t('catalog.tiers.saving') : t('catalog.tiers.save')}
      </button>
      {errorId && <span className={styles.tierError}>{t(errorId)}</span>}
    </div>
  );
}

export default function PriceTierPanel({ productId, tiers }) {
  return (
    <div className={styles.panel}>
      {TIER_ORDER.map((tier) => (
        <TierRow
          key={tier}
          productId={productId}
          tier={tier}
          existing={tiers.find((t) => t.tier === tier)}
        />
      ))}
    </div>
  );
}
